import { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getSelections } from 'src/shared/services/selectionService';

type SelectionOption = { id: number; name: string };

type InvoiceTemplateSelectionPickerProps = {
  readOnly?: boolean;
};

// Väljer mallens sparade urval (selection_id). 0 = inget urval valt.
export const InvoiceTemplateSelectionPicker = ({ readOnly = false }: InvoiceTemplateSelectionPickerProps) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { control, getValues, setValue } = useFormContext();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SelectionOption[]>([]);

  useEffect(() => {
    const fetchSelections = async () => {
      setLoading(true);
      try {
        const res = await getSelections(false, 0, 500, 'selectionName', 'asc');
        if (isMounted()) setOptions((res.data.results ?? []).map((s) => ({ id: s.selection_id, name: s.selectionName })));
      } catch (error) {
        if (isMounted()) ConsoleHelper.log(error);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchSelections();
  }, [isMounted]);

  return (
    <Controller
      name='selection_id'
      control={control}
      render={({ field }) => {
        // Om mallens urval inte finns bland de hämtade visas namnet som API:t skickade med mallen.
        const value = field.value ? options.find((o) => o.id === field.value) ?? { id: field.value, name: getValues('selectionName') ?? '' } : null;
        return (
          <Autocomplete
            disabled={readOnly}
            loading={loading}
            options={options}
            value={value}
            onChange={(_e, option) => {
              field.onChange(option?.id ?? 0);
              setValue('selectionName', option?.name ?? '');
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                sx={{ backgroundColor: 'white' }}
                label={t('invoice:adminInvoiceTemplatePage.templateInformation.savedSelection')}
                placeholder={t('member:memberAdvancedSearch.noSelection')}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        );
      }}
    />
  );
};
